import React from 'react'
import { FormScreen, FormValidationSchema, FormValues } from 'components/screen-templates/FormScreen'
import * as Yup from 'yup'
import { onBackClickCallback, onNextClickType } from 'components/types'
import { Quizzes } from 'redux/quiz/types'
import { GifName } from '../../data/gifs'
import { WeightUnit } from 'libs/weight/types'
import { Answer } from 'clients/quiz-service'
import { IntlShape, useIntl } from 'react-intl'
import { WeightScreenFormBody } from '../screen-components/WeightScreenFormBody'
import { getFormattedWeightValues } from '../../libs/weight/weight-helpers'
import { ZiggieAsk } from '../core/ziggie/ZiggieAsk'

export const WEIGHT_SCREEN_INITIAL_VALUES = {
  weight_kg: '',
  weight_lbs: '',
  weight_st_lbs: '',
  weight_st: '',
  weight_unit: 'lbs',
}

export type InputWeightScreenProps = {
  title: string
  description?: string
  topImage?: string
  topImageAlt?: string
  gif?: GifName
  buttonText?: string
  defaultWeightUnit: WeightUnit
  // Ziggie asks the question from a speech bubble, in place of the title and description.
  withZiggie?: { gif: GifName; typewriter?: boolean }
}

type Props = InputWeightScreenProps & {
  onBackClick?: onBackClickCallback
  onNextClick: onNextClickType
  quiz: Quizzes
}

export const InputWeightScreen: React.FC<Props> = ({
  title,
  description,
  topImage,
  topImageAlt,
  gif,
  buttonText,
  defaultWeightUnit,
  withZiggie,
  onNextClick,
  onBackClick,
  quiz,
}) => {
  const intl = useIntl()
  // Carry the metric/imperial choice forward from the height screen so users
  // who picked cm see kg here, and ft users keep the config default (lbs/stone).
  const derivedDefaultUnit: WeightUnit = quiz.height_unit === 'cm' ? 'kg' : defaultWeightUnit
  const savedUnit = (quiz.weight_unit as string) || derivedDefaultUnit
  const initialFormValues = {
    weight_kg: quiz.weight_kg ? String(quiz.weight_kg) : '',
    weight_lbs: quiz.weight_lbs ? String(quiz.weight_lbs) : '',
    weight_st: savedUnit === 'stone' && quiz.weight_lbs ? String(Math.floor(quiz.weight_lbs / 14)) : '',
    weight_st_lbs: savedUnit === 'stone' && quiz.weight_lbs ? String(quiz.weight_lbs % 14) : '',
    weight_unit: savedUnit,
  }

  const handleOnNextClick = (values?: FormValues | Answer[] | any) => {
    onNextClick(getFormattedWeightValues(values))
  }

  return (
    <FormScreen
      topImage={topImage}
      topImageAlt={topImageAlt}
      gif={gif}
      screenTitle={title}
      description={description}
      buttonText={buttonText}
      questionSlot={
        withZiggie && <ZiggieAsk gif={withZiggie.gif} text={title} quiz={quiz} typewriter={withZiggie.typewriter} />
      }
      form={{
        body: <WeightScreenFormBody defaultWeightUnit={savedUnit as WeightUnit} />,
        validationSchema: weightValidationSchema(intl),
        initialValues: initialFormValues,
      }}
      onNextClick={handleOnNextClick}
      onBackClick={onBackClick}
    />
  )
}

const GENERIC_VALUE_ERROR = 'Please enter a value to continue'

const weightValidationSchema = (intl: IntlShape): FormValidationSchema =>
  Yup.object({
    weight_unit: Yup.string().required(),

    weight_lbs: Yup.number().when('weight_unit', {
      is: (weight_unit: string) => weight_unit === 'lbs',
      then: () => Yup.number().typeError(GENERIC_VALUE_ERROR).required(GENERIC_VALUE_ERROR),
      otherwise: () => Yup.mixed().notRequired(),
    }),

    weight_kg: Yup.number().when('weight_unit', {
      is: (weight_unit: string) => weight_unit === 'kg',
      then: () => Yup.number().typeError(GENERIC_VALUE_ERROR).required(GENERIC_VALUE_ERROR),
      otherwise: () => Yup.mixed().notRequired(),
    }),

    weight_st: Yup.number().when('weight_unit', {
      is: (weight_unit: string) => weight_unit === 'stone',
      then: () => Yup.number().typeError(GENERIC_VALUE_ERROR).required(GENERIC_VALUE_ERROR),
      otherwise: () => Yup.mixed().notRequired(),
    }),

    weight_st_lbs: Yup.number().when('weight_unit', {
      is: (weight_unit: string) => weight_unit === 'stone',
      then: () =>
        Yup.number()
          .transform((value) => (isNaN(value) ? undefined : value))
          .notRequired()
          .min(0, 'You cannot enter a negative number')
          .max(13, intl.formatMessage({ id: 'quiz.weight_validation.lbs_in_stone' })),
      otherwise: () => Yup.mixed().notRequired(),
    }),
  })

export const simpleWeightValidationSchema = (intl: IntlShape): Record<string, Yup.AnySchema> => ({
  weight_unit: Yup.string().required(),

  weight_lbs: Yup.number().when('weight_unit', {
    is: (weight_unit: string) => weight_unit === 'lbs',
    then: () => Yup.number().typeError(GENERIC_VALUE_ERROR).required(GENERIC_VALUE_ERROR),
    otherwise: () => Yup.mixed().notRequired(),
  }),

  weight_kg: Yup.number().when('weight_unit', {
    is: (weight_unit: string) => weight_unit === 'kg',
    then: () => Yup.number().typeError(GENERIC_VALUE_ERROR).required(GENERIC_VALUE_ERROR),
    otherwise: () => Yup.mixed().notRequired(),
  }),

  weight_st: Yup.number().when('weight_unit', {
    is: (weight_unit: string) => weight_unit === 'stone',
    then: () => Yup.number().typeError(GENERIC_VALUE_ERROR).required(GENERIC_VALUE_ERROR),
    otherwise: () => Yup.mixed().notRequired(),
  }),

  weight_st_lbs: Yup.number().when('weight_unit', {
    is: (weight_unit: string) => weight_unit === 'stone',
    then: () =>
      Yup.number()
        .transform((value) => (isNaN(value) ? undefined : value))
        .notRequired()
        .min(0, 'You cannot enter a negative number')
        .max(13, intl.formatMessage({ id: 'quiz.weight_validation.lbs_in_stone' })),
    otherwise: () => Yup.mixed().notRequired(),
  }),
})
